/**
 * OnboardingPreferencesPage — Phase UX-E
 * Behind ENABLE_ONBOARDING_WIZARD_V2
 *
 * This page is shown to new users after signup/verification who have no
 * preferences or digest profiles yet.
 *
 * PHI-Zero: no user text is logged.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UnifiedPreferencesWizard from '../components/UnifiedPreferencesWizard';
import { HowItWorksRadial } from '../components/HowItWorksRadial';
import { preferencesAPI } from '../api';
import { Loader2 } from 'lucide-react';

const OnboardingPreferencesPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [existingProfile, setExistingProfile] = useState(null);
  const [existingProfileCount, setExistingProfileCount] = useState(0);

  const isPremiumOrTrial = user?.plan_tier === 'premium' || 
    (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date());

  // Check if user already has a profile
  useEffect(() => {
    const checkExistingProfile = async () => {
      try {
        const profiles = await preferencesAPI.listProfiles();
        if (profiles && profiles.length > 0) {
          setExistingProfileCount(profiles.length);
          // Use the primary profile, or the first one
          const primary = profiles.find((p: any) => p.is_primary) || profiles[0];
          setExistingProfile(primary);
        }
      } catch (error: any) {
        // If profiles feature is disabled, check legacy preferences
        if (error.response?.data?.detail?.error_code === 'feature_disabled') {
          console.log('Digest profiles disabled, checking legacy preferences');
          try {
            const legacyPrefs = await preferencesAPI.getMyPreferences() as any;
            if (legacyPrefs && legacyPrefs.specialty_id) {
              setExistingProfileCount(1);
              // Convert legacy prefs to profile-like object
              setExistingProfile({
                profile_id: 'legacy',
                name: 'My Digest',
                specialty_id: legacyPrefs.specialty_id,
                subspecialty_id: legacyPrefs.subspecialty_id,
                subspecialties: legacyPrefs.subspecialties,
                custom_keywords: legacyPrefs.custom_keywords || [],
                topics_selected: legacyPrefs.topics_selected || [],
                custom_topics: legacyPrefs.custom_topics || [],
                journals_selected: legacyPrefs.journals_selected || [],
                custom_journals: legacyPrefs.custom_journals || [],
                digest_frequency: legacyPrefs.digest_frequency || 'daily',
                delivery_time: legacyPrefs.delivery_time,
                schedule_timezone: legacyPrefs.schedule_timezone,
                day_of_week: legacyPrefs.day_of_week,
                day_of_month: legacyPrefs.day_of_month,
                max_articles_per_digest: legacyPrefs.max_articles_per_digest,
                email_notifications_enabled: legacyPrefs.email_notifications_enabled,
                is_primary: true,
              });
            }
          } catch (legacyError) {
            console.error('Failed to load legacy preferences:', legacyError);
          }
        } else {
          console.error('Failed to check existing profiles:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    checkExistingProfile();
  }, []);

  const handleComplete = async () => {
    // Refresh user state to get updated capabilities
    if (refreshUser) {
      await refreshUser();
    }
    navigate('/home');
  };

  const handleCancel = () => {
    // Allow skipping onboarding (user can set up later)
    navigate('/home');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="onboarding-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="onboarding-preferences-page">
      {/* How It Works - Show for new users (no existing profile) */}
      {!existingProfile && (
        <div className="max-w-3xl mx-auto mb-8">
          <HowItWorksRadial 
            variant="compact" 
            collapsible={false}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm"
          />
        </div>
      )}
      
      <UnifiedPreferencesWizard
        mode="onboarding"
        profile={existingProfile}
        onComplete={handleComplete}
        onCancel={handleCancel}
        isPremiumOrTrial={isPremiumOrTrial}
        user={user}
        existingProfileCount={existingProfileCount}
      />
    </div>
  );
};

export default OnboardingPreferencesPage;
